import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { waveBlockKindLabel, type WaveTocEntry } from "@vida/wave-core";
import {
  blockKindColor,
  blockKindGlyph,
  relationLabel,
} from "./waveFeedTokens";

const HOVER_DELAY_MS = 250;

type Props = {
  entries: WaveTocEntry[];
  activeBlockId?: string | null;
  /** Nested entries whose parent has hidden inner widgets. */
  dimmedBlockIds?: ReadonlySet<string>;
  onSelect: (entry: WaveTocEntry) => void;
};

type HoverState = {
  entry: WaveTocEntry;
  rect: DOMRect;
};

function formatTocDate(iso?: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" });
}

// La descrizione è un tooltip custom (non title= nativo): serve multiriga +
// styling. Portal fixed sul body: il nav ha overflow-y auto e occluderebbe.
function TocHoverCard({ hover }: { hover: HoverState }) {
  const meta: string[] = [];
  if (hover.entry.authorName) meta.push(hover.entry.authorName);
  const date = formatTocDate(hover.entry.createdAt);
  if (date) meta.push(date);

  const top = Math.max(
    8,
    Math.min(hover.rect.top, window.innerHeight - 180),
  );

  return createPortal(
    <div
      className="wave-thread-toc-hovercard"
      role="tooltip"
      style={{ left: hover.rect.right + 10, top }}
    >
      <span className="wave-thread-toc-hovercard-kind">
        {waveBlockKindLabel(hover.entry.kind)}
        {hover.entry.relation
          ? ` · ${relationLabel(hover.entry.relation)}`
          : ""}
      </span>
      {hover.entry.description && (
        <p className="wave-thread-toc-hovercard-desc">{hover.entry.description}</p>
      )}
      {meta.length > 0 && (
        <span className="wave-thread-toc-hovercard-meta">{meta.join(" · ")}</span>
      )}
    </div>,
    document.body,
  );
}

export function WaveThreadToc({
  entries,
  activeBlockId = null,
  dimmedBlockIds,
  onSelect,
}: Props) {
  const [hover, setHover] = useState<HoverState | null>(null);
  const hoverTimerRef = useRef<number | null>(null);

  const cancelHover = useCallback(() => {
    if (hoverTimerRef.current != null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setHover(null);
  }, []);

  const scheduleHover = useCallback(
    (entry: WaveTocEntry, target: HTMLElement) => {
      if (hoverTimerRef.current != null) {
        window.clearTimeout(hoverTimerRef.current);
      }
      hoverTimerRef.current = window.setTimeout(() => {
        hoverTimerRef.current = null;
        setHover({ entry, rect: target.getBoundingClientRect() });
      }, HOVER_DELAY_MS);
    },
    [],
  );

  useEffect(() => {
    return () => {
      if (hoverTimerRef.current != null) {
        window.clearTimeout(hoverTimerRef.current);
      }
    };
  }, []);

  if (entries.length === 0) return null;

  return (
    <nav className="wave-thread-toc" aria-label="Indice widget">
      <span className="wave-thread-toc-heading" aria-hidden>
        Indice
      </span>
      <ul className="wave-thread-toc-list">
        {entries.map((entry) => {
          const isActive = entry.blockId === activeBlockId;
          const isDimmed = dimmedBlockIds?.has(entry.blockId) ?? false;
          const color = blockKindColor(entry.kind);
          const classes = [
            "wave-thread-toc-item",
            entry.depth === 1 ? "wave-thread-toc-item--child" : "",
            isActive ? "is-active" : "",
            isDimmed ? "is-dimmed" : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <li key={entry.blockId}>
              <button
                type="button"
                className={classes}
                aria-current={isActive ? "true" : undefined}
                onClick={() => {
                  cancelHover();
                  onSelect(entry);
                }}
                onMouseEnter={(e) => scheduleHover(entry, e.currentTarget)}
                onMouseLeave={cancelHover}
              >
                <span
                  className="wave-thread-toc-glyph"
                  style={{
                    color,
                    background: `color-mix(in srgb, ${color} 15%, transparent)`,
                  }}
                  aria-hidden
                >
                  {blockKindGlyph(entry.kind)}
                </span>
                <span className="wave-thread-toc-title">{entry.title}</span>
                {entry.depth === 1 && entry.relation && (
                  <span className="wave-thread-toc-badge">
                    {relationLabel(entry.relation)}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
      {hover && <TocHoverCard hover={hover} />}
    </nav>
  );
}
